import { createApiClient, ApiError } from '@visep/api-sdk';

// baseUrl '' = mesma origem (proxy Vite em dev, Caddy em prod). O SDK prefixa /api.
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

// CSRF token vive só em memória (nunca persistido). Fonte de verdade do SDK
// autenticado — getCsrfToken lê esta binding viva.
let csrfToken: string | null = null;
let onExpired: () => void = () => {};
let currentFormId: string | undefined;

export function setCurrentFormId(id: string | undefined): void {
  currentFormId = id;
}
let refreshInFlight: Promise<boolean> | null = null;

export function setSessionCsrf(token: string | null): void {
  csrfToken = token;
}

export function bindOnExpired(cb: () => void): void {
  onExpired = cb;
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = (await res.json().catch(() => null)) as { csrfToken?: string } | null;
    if (data?.csrfToken) csrfToken = data.csrfToken;
    return true;
  } catch {
    return false;
  }
}

// Refresh deduplicado: o refresh token ROTACIONA no backend (reuse detection
// revoga a família). Chamadas concorrentes — boot do AuthProvider sob React
// StrictMode (efeito roda 2x) + interceptor de 401 — DEVEM compartilhar uma única
// requisição em voo, senão a 2ª manda o token já rotacionado e mata a sessão.
export function sessionRefresh(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// 403 csrf_invalid = CSRF em memória dessincronizado do cookie de sessão (outra
// aba/boot rotacionou o token). Recuperável com refresh — distinto de 'forbidden'.
async function isCsrfInvalid(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const data = (await res.clone().json()) as { error?: string } | null;
    return data?.error === 'csrf_invalid';
  } catch {
    return false;
  }
}

// Interceptor de 401 e de 403 csrf_invalid: refresh único compartilhado + retry 1x.
// Pula rotas /api/auth/* (login/refresh/logout) para não recursar.
export const authedFetch: typeof fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  let res = await fetch(input, init);
  if (url.includes('/api/auth/')) return res;
  if (res.status === 403) {
    try {
      const data = (await res.clone().json()) as { error?: string } | null;
      if (data?.error === 'password_change_required') {
        onExpired();
        window.location.href = '/login?reason=password_change_required';
        return res;
      }
    } catch { /* resposta não-JSON — ignorar, tratar como 403 genérico */ }
  }
  if (res.status !== 401 && !(await isCsrfInvalid(res))) return res;

  const refreshed = await sessionRefresh();
  if (!refreshed) {
    onExpired();
    return res;
  }

  const method = (init?.method ?? 'GET').toUpperCase();
  const headers = new Headers(init?.headers);
  if (method !== 'GET' && csrfToken) headers.set('X-CSRF-Token', csrfToken);
  res = await fetch(input, { ...init, headers });
  if (res.status === 401) onExpired();
  return res;
};

export const apiAuthed = createApiClient({
  baseUrl,
  getCsrfToken: () => csrfToken,
  getRequestId: () => currentFormId,
  fetchImpl: authedFetch,
});

export { ApiError };
